import { Order, CashRegister, CashRegisterTransaction, TaxRate, Product, ProductCategory } from './ModelRepository'
import Store from '@/store';
import User from '@/models/User';
import EinvoiceService from '@/services/EinvoiceService';
import i18n from '@/i18n';
import Printable from '@/utilities/Printable'
import ApiService from '@/services/ApiService';
import ReportPrintableGenerator from '@/utilities/ReportPrintableGenerator';

export class Report {

    constructor(options = {}, data = {}) {
        const { type, width, startTime, endTime, year = null, cashRegisterId, orders = null, filterOrders = {} } = options

        this.priceFormat = locale === 'sr' ? '0,0.00' : '0.00';
        this.type = type;
        this.width = width;
        this.startTime = startTime ? moment(startTime) : null;
        this.endTime = startTime ? moment(endTime) : null;
        this.year = year;
        this.cashRegister = CashRegister.find(cashRegisterId);
        this.filterOrders = filterOrders

        this.data = data

        const timezone = this.getTimezone()

        this.transactions = !this.startTime || !this.endTime ? [] : CashRegisterTransaction.all().filter(transaction => {
            const issuedAt = moment.utc(transaction.issued_at).tz(timezone);
            return issuedAt.isSameOrAfter(this.startTime) && issuedAt.isSameOrBefore(this.endTime)
        })

        if(orders) {
            this.orders = orders;
            return;
        }

        this.orders = !this.startTime || !this.endTime ? [] : Order.all().filter(order => {
            const lastActivityAt = moment.utc(order.last_activity_at).tz(timezone);
            const inRange = lastActivityAt.isSameOrAfter(this.startTime) && lastActivityAt.isSameOrBefore(this.endTime)

            if(!inRange) {
                return false
            }

            if(filterOrders.cashRegisterId && filterOrders.cashRegisterId != order.cash_register_id) {
                return false
            }

            if(filterOrders.clientId && filterOrders.clientId != order.client_id) {
                return false
            }

            return true
        })
    }

    static types(admin = false) {
        return [
            {
                type: 'current_state',
                category: 'fiscal',
                name: i18n.t('reports.current_state'),
                timeOptions: ['today'],
                enabled: true,
                fetchData: true
            },
            {
                type: 'current_state_legacy',
                category: 'legacy',
                name: i18n.t('reports.current_state_legacy'),
                timeOptions: ['today'],
                enabled: locale === 'ba',
                fetchData: false
            },
            {
                type: 'daily_report',
                category: 'fiscal',
                name: i18n.t('reports.daily_report'),
                timeOptions: ['yesterday', 'byDate'],
                enabled: true,
                fetchData: true
            },
            {
                type: 'daily_report_legacy',
                category: 'legacy',
                name: i18n.t('reports.daily_report_legacy'),
                timeOptions: ['today'],
                enabled: locale === 'ba',
                fetchData: false
            },
            {
                type: 'periodic_report',
                category: 'fiscal',
                name: i18n.t('reports.periodic_report'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: true,
                fetchData: true
            },
            {
                type: 'periodic_report_legacy',
                category: 'legacy',
                name: i18n.t('reports.periodic_report_legacy'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: locale === 'ba',
                fetchData: false
            },
            {
                type: 'shift_report',
                category: 'fiscal',
                name: i18n.t('reports.shift_report'),
                timeOptions: ['shift'],
                enabled: true,
                fetchData: true
            },
            {
                type: 'sales_by_user_detailed',
                category: 'sales',
                name: i18n.t('reports.sales_by_user_detailed'),
                timeOptions: ['shift'],
                enabled: !admin,
                fetchData: false
            },
            {
                type: 'einvoice_report',
                category: 'fiscal',
                name: i18n.t('reports.einvoice_report'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: EinvoiceService.isEnabled(),
                fetchData: true
            },
            {
                type: 'manual_register_report',
                category: 'default',
                name: i18n.t('reports.manual_register_report'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: false && locale === 'sr',
                fetchData: true
            },
            {
                type: 'electronic_journal',
                category: 'journal',
                name: i18n.t('reports.electronic_journal'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: locale === 'me',
                fetchData: true
            },
            {
                type: 'offline_electronic_journal',
                category: 'journal',
                name: i18n.t('reports.offline_electronic_journal'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: locale === 'me',
                fetchData: false
            },
            {
                type: 'corrective_electronic_journal',
                category: 'journal',
                name: i18n.t('reports.corrective_electronic_journal'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: locale === 'me',
                fetchData: true
            },
            {
                type: 'total_sales',
                category: 'sales',
                name: i18n.t('reports.total_sales'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: true,
                fetchData: true
            },
            {
                type: 'sales_by_client',
                category: 'sales',
                name: i18n.t('reports.sales_by_client'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: true,
                fetchData: true
            },
            {
                type: 'sales_by_user',
                category: 'sales',
                name: i18n.t('reports.sales_by_user'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: Store.getters.isOnline,
                fetchData: true
            },
            {
                type: 'sales_by_product',
                category: 'sales',
                name: i18n.t('reports.sales_by_product'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: Store.getters.isOnline,
                fetchData: true
            },
            {
                type: 'sales_by_product_category',
                category: 'sales',
                name: i18n.t('reports.sales_by_product_category'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: true,
                fetchData: true
            },
            {
                type: 'sales_by_product_type',
                category: 'sales',
                name: i18n.t('reports.sales_by_product_type'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: !isAllegra,
                fetchData: true
            },
            {
                type: 'sales_by_product_type_sent_to_room',
                category: 'sales',
                name: i18n.t('reports.sales_by_product_type_sent_to_room'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: !isAllegra && Store.getters.integrations.ota_sync,
                fetchData: true
            },
            {
                type: 'sales_by_product_type_and_taxes',
                category: 'sales',
                name: i18n.t('reports.sales_by_product_type_and_taxes'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: !isAllegra,
                fetchData: true
            },
            {
                type: 'sales_by_sector',
                category: 'sales',
                name: i18n.t('reports.sales_by_sector'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: Store.getters.isOnline && !isAllegra,
                fetchData: true
            },
            {
                type: 'sales_by_ingredient',
                category: 'sales',
                name: i18n.t('reports.sales_by_ingredient'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: Store.getters.isOnline && !isAllegra,
                fetchData: true
            },
            {
                type: 'product_quantities',
                category: 'default',
                name: i18n.t('reports.product_quantities'),
                timeOptions: [],
                enabled: true,
                fetchData: true
            },
            {
                type: 'bar_ledger',
                category: 'custom',
                name: i18n.t('reports.bar_ledger'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: !isAllegra && locale !== 'me',
                fetchData: true,
                width: '900px'
            }
        ]
    }

    getHeader() {
        return {
            operator_code: this.data.operator_code || Store.getters.user.hash,
            date: this.data.date || moment().format('YYYY-MM-DD'),
            time: this.data.time || moment().format('HH:mm:ss'),
            start_time: this.data.start_time || this.startTime?.format('YYYY-MM-DD HH:mm:ss') || null,
            end_time: this.data.end_time || this.endTime?.format('YYYY-MM-DD HH:mm:ss') || null,
        }
    }

    getBasicInfo() {
        if(this.data.basic_info) return this.data.basic_info

        const venue = Store.getters.tenant.venue

        let enu = ''
        if(this.cashRegister) {
            enu = locale === 'me' ? this.cashRegister.hash : (this.cashRegister.name || this.cashRegister.hash)
        }

        return {
            tax_payer_name: venue.company_name,
            tax_payer_address: venue.company_address,
            tax_id_number: venue.tax_id_number,
            venue: venue.name,
            venue_address: venue.address,
            enu: enu
        }
    }

    getCashCalculation() {
        if(this.data.cash_calculation) return this.data.cash_calculation

        const paid = this.orders.filter(order => order.isPaid())
        const advance = this.orders.filter(order => order.isAdvance() && !order.flagsInclude('closeAdvance'))
        const summary = this.orders.filter(order => order.isSummary())
        const unpaid = this.orders.filter(order => order.isUnpaid())
        const deposits = this.transactions.filter(transaction => transaction.getAmount().toUnit() >= 0)
        const withdraws = this.transactions.filter(transaction => transaction.getAmount().toUnit() < 0)

        const totalCashRegisterDeposit = deposits.reduce((acc, cur) => acc.add(cur.getAmount()), Money.parse(0))
        const totalCashRegisterWithdraws = withdraws.reduce((acc, cur) => acc.add(cur.getAmount().multiply(-1)), Money.parse(0))

        const totalSalesCash = [...paid, ...summary, ...advance].reduce((acc, cur) => acc.add(cur.getPaidCash()), Money.parse(0))
        const totalSalesCard = [...paid, ...summary, ...advance].reduce((acc, cur) => acc.add(cur.getPaidCreditCard()), Money.parse(0))
        const totalNonCashSales = [...paid, ...summary, ...advance].reduce((acc, cur) => acc.add(cur.getNonCashSale()), Money.parse(0))

        const totalCashRegisterCash = Money.parse(totalCashRegisterDeposit.toUnit()).subtract(totalCashRegisterWithdraws).add(totalSalesCash)

        const totalSalesOrders = locale === 'me' ? [...paid, ...unpaid, ...advance] : [...paid, ...advance];

        return {
            total_deposits: totalCashRegisterDeposit.toFormat(this.priceFormat),
            total_sales: totalSalesOrders.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
            cash_sales_wo_banknotes: totalSalesCard.toFormat(this.priceFormat),
            cash_sales_banknotes: totalSalesCash.toFormat(this.priceFormat),
            non_cash_sales: totalNonCashSales.toFormat(this.priceFormat),
            total_withdraws: totalCashRegisterWithdraws.toFormat(this.priceFormat),
            total_cash: totalCashRegisterCash.toFormat(this.priceFormat),
            order_sales: unpaid.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
        }
    }

    getSalesBreakdown() {
        if(this.data.sales_breakdown) return this.data.sales_breakdown;

        const paidOrders = this.orders.filter(order => order.isPaid() || (order.isSummary() && !order.canceled()) || order.isAdvance())
        const cashOrders = paidOrders.filter(order => !order.isNonCash())
        const nonCashOrders = paidOrders.filter(order => order.isNonCash())

        return {
            total_sales: {
                to_pay: paidOrders.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
                price: paidOrders.reduce((acc, cur) => acc.add(cur.getPrice()), Money.parse(0)).toFormat(this.priceFormat),
                discount_amount: paidOrders.reduce((acc, cur) => acc.add(cur.getDiscountAmount()), Money.parse(0)).toFormat(this.priceFormat),
            },
            cash_sales: {
                to_pay: cashOrders.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
                price: cashOrders.reduce((acc, cur) => acc.add(cur.getPrice()), Money.parse(0)).toFormat(this.priceFormat),
                discount_amount: cashOrders.reduce((acc, cur) => acc.add(cur.getDiscountAmount()), Money.parse(0)).toFormat(this.priceFormat),
            },
            non_cash_sales: {
                to_pay: nonCashOrders.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
                price: nonCashOrders.reduce((acc, cur) => acc.add(cur.getPrice()), Money.parse(0)).toFormat(this.priceFormat),
                discount_amount: nonCashOrders.reduce((acc, cur) => acc.add(cur.getDiscountAmount()), Money.parse(0)).toFormat(this.priceFormat),
            }
        }
    }

    getOrderBreakdown() {
        if(this.data.order_breakdown) return this.data.order_breakdown

        const corrective = this.orders.filter(order => order.correctsOthers())
        const advance = this.orders.filter(order => order.isAdvance())
        const unpaid = this.orders.filter(order => order.isUnpaid())
        const offline = this.orders.filter(order => !!order.sync_needed)

        const einvoiceSending = this.orders.filter(order => order.einvoiceIsSending())
        const einvoiceSent = this.orders.filter(order => order.einvoiceIsSent())
        const einvoiceApproved = this.orders.filter(order => order.einvoiceIsApproved())
        const einvoiceRejected = this.orders.filter(order => order.einvoiceIsRejected())
        const einvoiceCancelled = this.orders.filter(order => order.einvoiceIsCancelled())
        const einvoiceStorno = this.orders.filter(order => order.einvoiceIsStorno())

        return {
            corrective: {
                count: corrective.length,
                to_pay: corrective.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
                tax: corrective.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(this.priceFormat),
            },
            advance: {
                count: advance.length,
                to_pay: advance.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
                tax: advance.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(this.priceFormat),
            },
            unpaid: {
                count: unpaid.length,
                to_pay: unpaid.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
                tax: unpaid.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(this.priceFormat),
            },
            offline: {
                count: offline.length,
                to_pay: offline.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
                tax: offline.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(this.priceFormat),
            },
            sending: {
                count: einvoiceSending.length,
                to_pay: einvoiceSending.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
                tax: einvoiceSending.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(this.priceFormat),
            },
            sent: {
                count: einvoiceSent.length,
                to_pay: einvoiceSent.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
                tax: einvoiceSent.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(this.priceFormat),
            },
            approved: {
                count: einvoiceApproved.length,
                to_pay: einvoiceApproved.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
                tax: einvoiceApproved.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(this.priceFormat),
            },
            rejected: {
                count: einvoiceRejected.length,
                to_pay: einvoiceRejected.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
                tax: einvoiceRejected.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(this.priceFormat),
            },
            cancelled: {
                count: einvoiceCancelled.length,
                to_pay: einvoiceCancelled.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
                tax: einvoiceCancelled.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(this.priceFormat),
            },
            storno: {
                count: einvoiceStorno.length,
                to_pay: einvoiceStorno.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
                tax: einvoiceStorno.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(this.priceFormat),
            },
            /*
            * to be implemented
            * */
            /* order_copies: {
                count: null,
                to_pay: null,
                tax: null
            }, */
        }
    }

    getTaxes() {
        if(this.data.taxes) return this.data.taxes

        const rates = TaxRate.all().map(taxRate => taxRate.rate)
        const orderTaxes = this.orders.flatMap(order => order.taxes)

        const taxes = rates.map(rate => {
            const filtered = orderTaxes.filter(tax => tax.rate == rate)
            const totalTax = filtered.reduce((acc, cur) => acc.add(Money.parse(cur.value)), Money.parse(0))
            const totalPriceBeforeTax = locale !== 'sr'
                ? filtered.reduce((acc, cur) => acc.add(Money.parse(cur.price_before_tax)), Money.parse(0))
                : Money.parse(0)

            const sales = Money.parse(totalTax.toUnit()).add(totalPriceBeforeTax)

            return {
                rate: rate,
                total_tax: totalTax,
                total_price_before_tax: totalPriceBeforeTax,
                total: sales,
            }
        })

        taxes.push({
            rate: 'total',
            total_tax: taxes.reduce((acc, cur) => acc.add(cur.total_tax), Money.parse(0)),
            total_price_before_tax: taxes.reduce((acc, cur) => acc.add(cur.total_price_before_tax), Money.parse(0)),
            total: taxes.reduce((acc, cur) => acc.add(cur.total), Money.parse(0)),
        })

        return taxes.map(tax => {
            tax.total_tax = tax.total_tax.toFormat(this.priceFormat)
            tax.total_price_before_tax = tax.total_price_before_tax.toFormat(this.priceFormat)
            tax.total = tax.total.toFormat(this.priceFormat)
            return tax
        })
    }

    getPayments() {
        if(this.data.payments) return this.data.payments

        const paid = this.orders.filter(order => order.isMoneyExchanged())
        const unpaid = this.orders.filter(order => order.isUnpaid())

        return {
            cash: {
                banknote: paid.reduce((acc, cur) => acc.add(cur.getPaidCash()), Money.parse(0)).toFormat(this.priceFormat),
                card: paid.reduce((acc, cur) => acc.add(cur.getPaidCreditCard()), Money.parse(0)).toFormat(this.priceFormat),
                order: unpaid.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
                tip: paid.reduce((acc, cur) => acc.add(cur.getTip()), Money.parse(0)).toFormat(this.priceFormat),
                other_cash: Money.parse(0).toFormat(this.priceFormat),
            },
            noncash: {
                account: paid.reduce((acc, cur) => acc.add(cur.getPaidInvoice()), Money.parse(0)).toFormat(this.priceFormat),
                business_card: paid.reduce((acc, cur) => acc.add(cur.getPaidCreditCardBusiness()), Money.parse(0)).toFormat(this.priceFormat),
                advance: paid.reduce((acc, cur) => acc.add(cur.getPaidAdvance()), Money.parse(0)).toFormat(this.priceFormat),
                check: paid.reduce((acc, cur) => acc.add(cur.getPaidCheck()), Money.parse(0)).toFormat(this.priceFormat),
                voucher: paid.reduce((acc, cur) => acc.add(cur.getPaidVoucher()), Money.parse(0)).toFormat(this.priceFormat),
                mobile_money: paid.reduce((acc, cur) => acc.add(cur.getPaidMobileMoney()), Money.parse(0)).toFormat(this.priceFormat),
                other: paid.reduce((acc, cur) => acc.add(cur.getPaidOtherNoncash()), Money.parse(0)).toFormat(this.priceFormat),
                company: Money.parse(0).toFormat(this.priceFormat),
                order: Money.parse(0).toFormat(this.priceFormat),
                factoring: Money.parse(0).toFormat(this.priceFormat),
            }
        }
    }

    getTotalSales() {
        const sales = []

        const paid = this.orders.filter(order => order.isPaid())
        const unpaid = this.orders.filter(order => order.isUnpaid())
        const orders = locale === 'me' ? [...paid, ...unpaid] : paid

        const items = orders.flatMap(o => o.items)
        const salesGroup = Report.calculateSales(items)
        salesGroup.group = 'inventory_item'
        sales.push(salesGroup)

        return sales
    }

    getSalesByClient() {
        const sales = []

        const paid = this.orders.filter(order => order.isPaid())
        const unpaid = this.orders.filter(order => order.isUnpaid())
        const orders = locale === 'me' ? [...paid, ...unpaid] : paid

        const items = orders.flatMap(o => o.items)
        const salesGroup = Report.calculateSales(items)
        salesGroup.group = 'inventory_item'
        sales.push(salesGroup)

        return sales
    }

    getSalesByProductCategory() {
        const sales = []

        const paid = this.orders.filter(order => order.isPaid())
        const unpaid = this.orders.filter(order => order.isUnpaid())
        const orders = locale === 'me' ? [...paid, ...unpaid] : paid

        ProductCategory.all().forEach(category => {
            const items = orders.flatMap(o => o.items)
                .filter(item => {
                    const product = Product.all().find(product => product.id === item.product_id)
                    return product && product.category_id === category.id
                })

            const salesGroup = Report.calculateSales(items)
            salesGroup.group = category.name

            sales.push(salesGroup)
        })

        return sales
    }

    getSalesByProductType() {
        const sales = []

        const paid = this.orders.filter(order => order.isPaid())
        const unpaid = this.orders.filter(order => order.isUnpaid())
        const orders = locale === 'me' ? [...paid, ...unpaid] : paid

        const productTypes = Store.state.appConfig.product.types

        productTypes.forEach(productType => {
            const items = orders.flatMap(o => o.items)
                .filter(item => {
                    const product = Product.all().find(product => product.id === item.product_id)
                    return product && product.type === productType
                })

            const salesGroup = Report.calculateSales(items)
            salesGroup.group = productType

            sales.push(salesGroup)
        })

        return sales
    }

    getSalesByProductTypeAndTaxes() {
        const sales = []

        const paid = this.orders.filter(order => order.isPaid())
        const unpaid = this.orders.filter(order => order.isUnpaid())
        const orders = locale === 'me' ? [...paid, ...unpaid] : paid

        const productTypes = Store.state.appConfig.product.types

        productTypes.forEach(productType => {
            const items = orders.flatMap(o => o.items)
                .filter(item => {
                    const product = Product.all().find(product => product.id === item.product_id)
                    return product && product.type === productType
                })

            TaxRate.all().forEach(taxRate => {
                const taxRateItems = items.filter(item => parseFloat(item.tax_rate) === parseFloat(taxRate.rate))
                if(taxRateItems.length) {
                    sales.push({
                        group: `${productType}/${parseFloat(taxRate.rate)}%`,
                        items: [],
                        to_pay: taxRateItems.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(this.priceFormat),
                        tax: taxRateItems.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(this.priceFormat),
                        price_before_tax: taxRateItems.reduce((acc, cur) => acc.add(cur.getPriceBeforeTax()), Money.parse(0)).toFormat(this.priceFormat),
                    })
                }
            })
        })

        return sales
    }

    getSalesByUserDetailed() {
        const sales = []

        const users = this.filterOrders.userId ? [User.find(this.filterOrders.userId)] : User.all()

        users.forEach(user => {
            const userOrders = this.orders.filter(order => {
                const userId = Number(order.getFlag('clearDebtForUser')[0]) || order.user_id
                return userId == user.id
            })

            const paid = userOrders.filter(order => order.isPaid())
            const unpaid = userOrders.filter(order => order.isUnpaid())
            const orders = locale === 'me' ? [...paid, ...unpaid] : paid

            const items = orders.flatMap(o => o.items)
            const salesGroup = Report.calculateSales(items)

            salesGroup.group = 'inventory_item'
            salesGroup.heading = user.name

            salesGroup.stats = Order.generateStats(userOrders);
            salesGroup.stats.user_id = user.id;

            salesGroup.stats.cash.deposit = user.getDeposit().toFormat(this.priceFormat);
            salesGroup.stats.cash.debt_cleared = user.getCharged().multiply(-1).toFormat(this.priceFormat);
            salesGroup.stats.cash.debt = user.getDebt().toFormat(this.priceFormat);
            salesGroup.stats.cash.tip = userOrders.reduce((acc, cur) => acc.add(cur.getTip()), Money.parse(0)).toFormat(this.priceFormat);

            sales.push(salesGroup)
        })

        return sales
    }

    getProductQuantities() {
        return [
            {
                'heading': null,
                'columns': [
                    { 'key': 'inventory_item', 'align': 'L' },
                    { 'key': 'quantity', 'align': 'R' }
                ],
                'items': Product.all().map(p => {
                    return {
                        'key': p.name,
                        'values': [ p.getCount() ],
                        'additional': []
                    }
                }),
                'summary': []
            }
        ]
    }

    getSales() {
        if(this.data.sales) return this.data.sales

        const methodName = 'get' + this.type.replace(/(_|^)(\w)/g, (match, p1, p2) => p2.toUpperCase());
        if (typeof this[methodName] === 'function') {
            return this[methodName]();
        }

        return [];
    }

    getData() {
        if(this.data.data) return this.data.data

        const methodName = 'get' + this.type.replace(/(_|^)(\w)/g, (match, p1, p2) => p2.toUpperCase());
        if (typeof this[methodName] === 'function') {
            return this[methodName]();
        }

        return [];
    }

    static calculateSales(items) {
        const priceFormat = locale === 'sr' ? '0,0.00' : '0.00';

        let sales = {
            price: null,
            discount_amount: null,
            to_pay: null,
            items: []
        }

        const products = Product.all()

        items.forEach(orderItem => {
            let addedItem = sales.items.find(item => {
                return item.product_id
                    && item.product_id === orderItem.product_id
                    && item.unit_price === orderItem.getUnitPrice().toFormat('0.0000')
                    && item.discount_amount === orderItem.getDiscountAmount().toFormat('0.0000')
            });

            const product = products.find(product => product.id == orderItem.product_id)

            if (!addedItem) {
                sales.items.push({
                    product_id: orderItem.product_id,
                    name: product ? product.name : orderItem.name,
                    code: product ? product.code : '/',
                    in_stock: product ? product.count : '/',
                    count: orderItem.getCount().toFixed(3),
                    unit_price: orderItem.getUnitPrice().toFormat('0.0000'),
                    price: orderItem.getPrice().toFormat('0.0000'),
                    to_pay: orderItem.getToPay().toFormat('0.0000'),
                    discount_amount: orderItem.getDiscountAmount().toFormat('0.0000'),
                });
            } else {
                addedItem.count = _.round(parseFloat(addedItem.count) + orderItem.getCount(), 3).toFixed(3);

                addedItem.price = Money.parse(addedItem.price)
                    .add(orderItem.getPrice())
                    .toFormat('0.0000')

                addedItem.to_pay = Money.parse(addedItem.to_pay)
                    .add(orderItem.getToPay())
                    .toFormat('0.0000')

                addedItem.discount_amount = Money.parse(addedItem.discount_amount)
                    .add(orderItem.getDiscountAmount())
                    .toFormat('0.0000')
            }
        });

        sales.price = sales.items
            .reduce((acc, cur) => acc.add(Money.parse(Money.parse(cur.price).toFormat('0.00'))), Money.parse(0))
            .toFormat(priceFormat)

        sales.to_pay = sales.items
            .reduce((acc, cur) => acc.add(Money.parse(Money.parse(cur.to_pay).toFormat('0.00'))), Money.parse(0))
            .toFormat(priceFormat)

        sales.discount_amount = sales.items
            .reduce((acc, cur) => acc.add(Money.parse(Money.parse(cur.discount_amount).toFormat('0.00'))), Money.parse(0))
            .toFormat(priceFormat)

        sales.items = sales.items.map(item => {
            item.in_stock = _.round(item.in_stock, 3)
            item.count = _.round(item.count, 3)
            item.price = Money.parse(item.price).toFormat(priceFormat)
            item.to_pay = Money.parse(item.to_pay).toFormat(priceFormat)
            item.discount_amount = Money.parse(item.discount_amount).toFormat(priceFormat)
            return item
        })

        return sales;
    }

    getElectronicJournal(journalType = 'all') {
        if(this.data.journal_items) return this.data.journal_items

        const orders = this.orders
            .filter(order => {
                return journalType === 'all'
                    || (journalType === 'offline' && !!order.sync_needed)
                    || (journalType === 'corrective' && order.correctsOthers())
            }).map(order => {
                return {
                    item: order.$toJson(),
                    timestamp: order.getLastActivityAt(),
                    type: 'order'
                }
            })

        const transactions = this.transactions
            .filter(transaction => {
                if(journalType === 'corrective') return false
                return journalType === 'all' || (journalType === 'offline' && !!transaction.sync_needed)
            }).map(transaction => {
                return {
                    item: transaction.$toJson(),
                    timestamp: transaction.getIssuedAt(),
                    type: 'cashTransaction'
                }
            })

        return [...orders, ...transactions]
            .sort((a, b) => a.timestamp.valueOf() > b.timestamp.valueOf() ? 1 : -1)
    }

    getFooter() {
        const workingDays = Store.getters.workingDays

        let businessDayNumber = workingDays.length && this.startTime ? workingDays.indexOf(this.startTime.format('YYYY-MM-DD')) + 1 : ''
        let firstWorkingDay = workingDays.length && this.startTime ? workingDays.indexOf(this.startTime.format('YYYY-MM-DD')) + 1 : ''
        let lastWorkingDay = workingDays.length && this.endTime ? workingDays.indexOf(this.endTime.format('YYYY-MM-DD')) + 1 : ''

        const footer = {}

        if(this.type === 'daily_report') {
            footer.business_day_number = this.data.business_day_number || businessDayNumber
        } else if(this.type === 'periodic_report') {
            footer.business_day_number = this.data.business_day_number || businessDayNumber
            footer.first_business_day = this.data.first_business_day || firstWorkingDay
            footer.last_business_day = this.data.last_business_day || lastWorkingDay
            footer.first_order_number = this.data.first_order_number || (this.orders.length ? (this.orders[0].number || '') : '')
            footer.last_order_number = this.data.last_order_number || (this.orders.length ? (this.orders[this.orders.length - 1].number || '') : '')
        }

        return footer
    }

    static async create(options = {}) {
        const {
            type,
            width,
            startTime,
            endTime,
            year = null,
            cashRegisterId = null,
            orders = null,
            filterOrders = {},
            admin = false
        } = options;

        const reportType = Report.types().find(r => r.type === type)
        if(!reportType) return null;

        const report = new Report({
            type,
            width,
            startTime,
            endTime,
            cashRegisterId,
            year,
            orders,
            filterOrders
        });

        const isOnline = Store.getters.isOnline;
        const generateOffline = !year && (!isOnline || !reportType.fetchData);

        if (generateOffline) {
            return report;
        }

        try {
            await report.fetchData(admin);
            return report.data ? report : null;
        } catch (error) {
            console.log(error.message);
            return null;
        }
    }

    async fetchData(admin = false) {
        const url = admin ? '/admin/reports' : '/report'
        const params = {
            type: this.type,
            start_time: this.startTime?.format('YYYY-MM-DD HH:mm:ss') || null,
            end_time: this.endTime?.format('YYYY-MM-DD HH:mm:ss') || null,
            cash_register_id: this.cashRegister?.id || null,
            client_id: this.filterOrders.clientId,
            products: this.filterOrders.products,
        }

        if(this.year) {
            params.year = this.year;
        }

        const response = await ApiService.get(url, { params: params, timeout: 300000 })
        this.data = response.data;
    }

    generate() {
        const reportType = Report.types().find(r => r.type === this.type)

        let reportData = {
            ...this.getHeader(),
            ...this.getFooter(),
            type: this.type,
            basic_info: this.getBasicInfo()
        }

        if(reportType.category === 'sales') {
            reportData.sales = this.getSales()
        } else if(reportType.category === 'journal') {
            const journalType = this.type.replace('electronic_journal', '').replace('_', '') || 'all'
            reportData.journal_items = this.getElectronicJournal(journalType)
        } else if(reportType.category === 'fiscal') {
            reportData.order_breakdown = this.getOrderBreakdown()
            reportData.taxes = this.getTaxes()
            reportData.payments = this.getPayments()
            reportData.cash_calculation = this.getCashCalculation()
            reportData.sales_breakdown = this.getSalesBreakdown()
        } else {
            reportData.data = this.getData()
        }

        return reportData
    }

    /**
     * Generate the printable object
     */
    getPrintable() {
        const reportData = this.generate()

        const reportType = Report.types().find(r => r.type === this.type)
        if(reportType.category === 'custom') {
            const reportPrintableGenerator = new ReportPrintableGenerator(this.type, reportData)
            return reportPrintableGenerator.getPrintable()
        }

        const header = this.getHeaderPrintable(reportData)
        const basicInfo = this.getBasicInfoPrintable(reportData)
        const cashCalculation = this.getCashCalculationPrintable(reportData)
        const salesBreakdown = this.getSalesBreakdownPrintable(reportData)
        const orderBreakdown = this.getOrderBreakdownPrintable(reportData)
        const sales = this.getSalesPrintable(reportData)
        const data = this.getDataPrintable(reportData)
        const taxes = this.getTaxesPrintable(reportData)
        const payments = this.getPaymentsPrintable(reportData)
        const journal = this.getJournalPrintable(reportData)
        const additionalFooter = this.getAdditionalFooterPrintable(reportData)
        const footer = this.getFooterPrintable(reportData)

        const pr = new Printable()
        pr.l('D', '', { delimiter: '=' })

        const isJournal = [
            'electronic_journal',
            'offline_electronic_journal',
            'corrective_electronic_journal'
        ].includes(this.type)

        let printableContent = isJournal ? [
            ...header.getPrintableContent(),
            ...basicInfo.getPrintableContent(),
            ...journal.getPrintableContent(),
            ...additionalFooter.getPrintableContent(),
            ...footer.getPrintableContent(),
        ] : [
            ...header.getPrintableContent(),
            ...basicInfo.getPrintableContent(),
            ...cashCalculation.getPrintableContent(),
            ...salesBreakdown.getPrintableContent(),
            ...orderBreakdown.getPrintableContent(),
            ...sales.getPrintableContent(),
            ...data.getPrintableContent(),
            ...taxes.getPrintableContent(),
            ...payments.getPrintableContent(),
            ...additionalFooter.getPrintableContent(),
            ...footer.getPrintableContent()
        ]

        pr.addPrintableContent(printableContent)
        pr.l('D', '', { delimiter: '=' })

        return pr
    }

    getHeaderPrintable(reportData) {
        const pr = new Printable()

        let header = []
        pr.l('L', i18n.t('reports.' + reportData.type), { align: 'C' })
        header.push(`[C]`)
        if(reportData.start_time && reportData.end_time) {
            pr.l('L', `${i18n.t('general.from')}: ${this.formatDateTime(reportData.start_time)}`, { align: 'C' })
            pr.l('L', `${i18n.t('general.to')}: ${this.formatDateTime(reportData.end_time)}`, { align: 'C' })
        }
        pr.l('D', '', { delimiter: '-' })

        return pr
    }

    getBasicInfoPrintable(reportData) {
        const pr = new Printable()

        if(!reportData.basic_info) return pr

        pr.l('L', `${i18n.t('reports.body.basic_info.tax_payer_name')}: ${reportData.basic_info.tax_payer_name}`)
        pr.l('B', `${i18n.t('reports.body.basic_info.tax_payer_address')}:`, reportData.basic_info.tax_payer_address)
        pr.l('L', `${i18n.t('reports.body.basic_info.tax_id_number')}: ${reportData.basic_info.tax_id_number}`)
        pr.l('L', `${i18n.t('reports.body.basic_info.venue')}: ${reportData.basic_info.venue}`)
        pr.l('B', `${i18n.t('reports.body.basic_info.venue_address')}:`, reportData.basic_info.venue_address)

        if(reportData.basic_info.enu) {
            pr.l('L', `${i18n.t('reports.body.basic_info.enu')}: ${reportData.basic_info.enu}`)
        }

        pr.l('D', '', { delimiter: '-' })

        return pr
    }

    getCashCalculationPrintable(reportData) {
        const pr = new Printable()

        const noncashOnly = !!Store.getters.tenant?.venue?.settings?.noncash_only;
        const einvoiceReport = reportData.type === 'einvoice_report';

        if(!reportData.cash_calculation || noncashOnly || einvoiceReport) return pr

        pr.l('S', `${i18n.t('reports.body.cash_calculation.total_cash')}:`, reportData.cash_calculation.total_cash)
        pr.l('S', `${i18n.t('reports.body.cash_calculation.deposit')}:`, reportData.cash_calculation.total_deposits)
        pr.l('S', `${i18n.t('reports.body.cash_calculation.total_withdraws')}:`, reportData.cash_calculation.total_withdraws)
        pr.l('S', `${i18n.t('reports.body.cash_calculation.cash_sales_banknotes')}:`, reportData.cash_calculation.cash_sales_banknotes)

        if(locale === 'me') {
            pr.l('S', `${i18n.t('reports.body.cash_calculation.cash_sales_wo_banknotes')}:`, reportData.cash_calculation.cash_sales_wo_banknotes)
            pr.l('S', `${i18n.t('reports.body.cash_calculation.non_cash_sales')}:`, reportData.cash_calculation.non_cash_sales)
            pr.l('S', `${i18n.t('reports.body.cash_calculation.total_sales')}:`, reportData.cash_calculation.total_sales)
        }

        pr.l('D', '', { delimiter: '-' })

        return pr
    }

    getSalesBreakdownPrintable(reportData) {
        const pr = new Printable()

        if(!reportData.sales_breakdown) return pr

        const noncashOnly = !!Store.getters.tenant?.venue?.settings?.noncash_only;
        const einvoiceReport = reportData.type === 'einvoice_report';

        if(reportData.sales_breakdown.cash_sales && !noncashOnly && !einvoiceReport) {
            pr.l('L', i18n.t('reports.body.sales_breakdown.cash_sales'), { align: 'C' })
            pr.l('D', '', { delimiter: '-' })
            pr.l('S', `${i18n.t('reports.body.sales_breakdown.price')}:`, reportData.sales_breakdown.cash_sales.price)
            pr.l('S', `${i18n.t('reports.body.sales_breakdown.discount')}:`, reportData.sales_breakdown.cash_sales.discount_amount)
            pr.l('S', `${i18n.t('reports.body.sales_breakdown.to_pay')}:`, reportData.sales_breakdown.cash_sales.to_pay)
            pr.l('D', '', { delimiter: '-' })
        }
        if(reportData.sales_breakdown.non_cash_sales && !noncashOnly && !einvoiceReport) {
            pr.l('L', i18n.t('reports.body.sales_breakdown.non_cash_sales'), { align: 'C' })
            pr.l('D', '', { delimiter: '-' })
            pr.l('S', `${i18n.t('reports.body.sales_breakdown.price')}:`, reportData.sales_breakdown.non_cash_sales.price)
            pr.l('S', `${i18n.t('reports.body.sales_breakdown.discount')}:`, reportData.sales_breakdown.non_cash_sales.discount_amount)
            pr.l('S', `${i18n.t('reports.body.sales_breakdown.to_pay')}:`, reportData.sales_breakdown.non_cash_sales.to_pay)
            pr.l('D', '', { delimiter: '-' })
        }
        if(reportData.sales_breakdown.total_sales) {
            pr.l('L', i18n.t('reports.body.sales_breakdown.total_sales'), { align: 'C' })
            pr.l('D', '', { delimiter: '-' })
            pr.l('S', `${i18n.t('reports.body.sales_breakdown.price')}:`, reportData.sales_breakdown.total_sales.price)
            pr.l('S', `${i18n.t('reports.body.sales_breakdown.discount')}:`, reportData.sales_breakdown.total_sales.discount_amount)
            pr.l('S', `${i18n.t('reports.body.sales_breakdown.to_pay')}:`, reportData.sales_breakdown.total_sales.to_pay)
            pr.l('D', '', { delimiter: '-' })
        }

        return pr
    }

    getOrderBreakdownPrintable(reportData) {
        const pr = new Printable()

        if(!reportData.order_breakdown) return pr

        const einvoiceReport = reportData.type === 'einvoice_report';

        const orderCategories = einvoiceReport
            ? ['sending', 'sent', 'approved', 'rejected', 'cancelled', 'storno']
            : ['corrective', 'advance', 'offline', 'manual_register', 'order_copies']

        if(locale === 'me') {
            orderCategories.push('unpaid')
        }

        orderCategories.forEach(category => {
            if(reportData.order_breakdown[category]) {
                pr.l('L', `${i18n.t(`reports.body.order_breakdown.${category}`)}`, { align: 'C' })
                pr.l('D', '', { delimiter: '-' })
                pr.l('S', `${i18n.t('reports.body.order_breakdown.count')}:`, reportData.order_breakdown[category].count)
                pr.l('S', `${i18n.t('reports.body.order_breakdown.to_pay')}:`, reportData.order_breakdown[category].to_pay)
                pr.l('S', `${i18n.t('reports.body.order_breakdown.tax')}:`, reportData.order_breakdown[category].tax)
                pr.l('D', '', { delimiter: '-' })
            }
        })

        return pr
    }

    getSalesPrintable(reportData) {
        const pr = new Printable()

        if(!reportData.sales) return pr

        const noncashOnly = !!Store.getters.tenant?.venue?.settings?.noncash_only;

        const getTotalKeys = (salesGroup) => Object.keys(salesGroup).filter(key => {
            return salesGroup[key] !== null && !isNaN((salesGroup[key] + '').replace(/,/g, '')) && key !== 'group' && key !== 'items' && key !== 'heading'
        })

        reportData.sales
            .filter(salesGroup => getTotalKeys(salesGroup).length || salesGroup.items.length || salesGroup.stats)
            .forEach(salesGroup => {
                if(salesGroup.heading) {
                    pr.l('L', salesGroup.heading, { align: 'C' })
                    pr.l('D', '', { delimiter: '-' })
                }

                const zero = Money.parse(0).toFormat(this.priceFormat)

                if(salesGroup.stats && !noncashOnly) {
                    pr.l('S', `${i18n.t('reports.body.sales.stats.cash.deposit')}:`, salesGroup.stats.cash.deposit || zero)
                    pr.l('S', `${i18n.t('reports.body.sales.stats.cash.sold')}:`, salesGroup.stats.cash.sold || zero)
                    pr.l('S', `${i18n.t('reports.body.sales.stats.cash.refunded')}:`, salesGroup.stats.cash.refunded || zero)
                    pr.l('S', `${i18n.t('reports.body.sales.stats.cash.debt_cleared')}:`, salesGroup.stats.cash.debt_cleared || zero)
                    pr.l('S', `${i18n.t('reports.body.sales.stats.cash.debt')}:`, salesGroup.stats.cash.debt || zero)
                    pr.l('S', `${i18n.t('reports.body.sales.stats.cash.tip')}:`, salesGroup.stats.cash.tip || zero)
                    pr.l('D', '', { delimiter: '-' })

                    pr.l('S', `${i18n.t('reports.body.sales.stats.payments.cash')}:`, salesGroup.stats.payments.cash || zero)
                    pr.l('S', `${i18n.t('reports.body.sales.stats.payments.credit_card')}:`, salesGroup.stats.payments.credit_card || zero)
                    pr.l('S', `${i18n.t('reports.body.sales.stats.payments.invoice')}:`, salesGroup.stats.payments.invoice || zero)

                    if(locale === 'me') {
                        pr.l('S', `${i18n.t('reports.body.sales.stats.payments.credit_card_business')}:`, salesGroup.stats.payments.credit_card_business || zero)
                    }

                    pr.l('S', `${i18n.t('reports.body.sales.stats.payments.voucher')}:`, salesGroup.stats.payments.voucher || zero)

                    if(locale === 'sr') {
                        pr.l('S', `${i18n.t('reports.body.sales.stats.payments.check')}:`, salesGroup.stats.payments.check || zero)
                        pr.l('S', `${i18n.t('reports.body.sales.stats.payments.mobile_money')}:`, salesGroup.stats.payments.mobile_money || zero)
                    }

                    pr.l('S', `${i18n.t('reports.body.sales.stats.payments.other_noncash')}:`, salesGroup.stats.payments.other_noncash || zero)
                    pr.l('D', '', { delimiter: '-' })
                    pr.l('S', `${i18n.t('reports.body.sales.stats.payments.advance')}:`, salesGroup.stats.payments.advance || zero)
                    pr.l('D', '', { delimiter: '-' })

                    pr.l('S', `${i18n.t('reports.body.sales.stats.total.sold')}:`, salesGroup.stats.total.sold || zero)
                    pr.l('S', `${i18n.t('reports.body.sales.stats.total.refunded')}:`, salesGroup.stats.total.refunded || zero)
                    pr.l('S', `${i18n.t('reports.body.sales.stats.total.unpaid')}:`, salesGroup.stats.total.unpaid || zero)
                    pr.l('D', '', { delimiter: '-' })
                }

                const group = salesGroup.group?.split('/')[0] || 'other'
                const groupSuffix = salesGroup.group?.split('/')[1] || ''
                let groupLabel = i18n.t(`reports.body.sales.group.${group}`) !== `reports.body.sales.group.${group}`
                    ? i18n.t(`reports.body.sales.group.${group}`)
                    : group
                if(groupSuffix) {
                    groupLabel += ' ' + groupSuffix
                }

                pr.l('S', groupLabel, i18n.t('general.sales'))
                pr.l('D', '', { delimiter: '-' })

                // Filter out sale items that have no count
                if (
                    salesGroup.items.length > 0 &&
                    salesGroup.items.every((item) => Object.hasOwnProperty.call(item, 'count'))
                ) {
                    salesGroup.items = salesGroup.items.filter((item) => item.count !== 0);
                }

                salesGroup.items.forEach(item => {
                    const itemLabel = i18n.t(`reports.body.sales.group.item.${item.name}`) !== `reports.body.sales.group.item.${item.name}`
                        ? i18n.t(`reports.body.sales.group.item.${item.name}`)
                        : item.name

                    pr.l('S', `${item.count ? _.round(item.count, 3) + ' x ' : ''}${itemLabel}`, item.price || '')

                    if(item.code) {
                        pr.l('L', `    ${i18n.t('general.code')}: ${item.code}`)
                    }

                    if(item.discount_amount && item.discount_amount.replace(/,/g, '') > 0) {
                        pr.l('L', `    ${i18n.t('general.discount')}: ${item.discount_amount}`)
                    }

                    if (item.additional?.length) {
                        item.additional.forEach((a) => {
                            const count = a.count ? _.round(a.count, 3) : '';
                            const price = a.price ? ` x ${_.round(a.price, 2)}` : '';

                            pr.l('L', `    ${count}${price}`);
                        });
                    }

                    if(!Store.getters.tenant.venue.settings.hide_product_quantities && item.in_stock) {
                        pr.l('L', `    ${i18n.t('general.in_stock')}: ${_.round(parseFloat(item.in_stock), 3).toFixed(3)}`);
                    }
                })

                if(salesGroup.items.length) {
                    pr.l('D', '', { delimiter: '-' })
                }

                const total = getTotalKeys(salesGroup)

                if(total.length) {
                    total.forEach(key => {
                        pr.l('S', `${i18n.t(`reports.body.sales.${key}`)}:`, salesGroup[key])
                    })

                    pr.l('D', '', { delimiter: '-' })
                }
        })

        return pr
    }

    getDataPrintable(reportData) {
        const pr = new Printable()

        if(!reportData.data) return pr

        reportData.data.forEach(dataGroup => {
            if(dataGroup.heading) {
                pr.l('L', this.t('reports.body.data.heading', dataGroup.heading), { align: 'C' })
                pr.l('D', '', { delimiter: '-' })
            }

            const createTableHeading = dataGroup.columns.some(col => col.key)
            if(createTableHeading) {
                pr.table({ colNumber: dataGroup.columns.length })

                let tableHeading = '';
                dataGroup.columns.forEach(col => {
                    const key = col.key?.split('/')[0]
                    if(key) {
                        const suffix = col.key.split('/')[1]
                        let colLabel = this.t('reports.body.data.columns', key)
                        if(suffix) {
                            colLabel += ' ' + colLabel
                        }
                        tableHeading += colLabel
                    }

                    pr.tl({ content: tableHeading, align: col.align })
                    tableHeading = ''
                })

                pr.tl({ content: 'divider' })
            }

            dataGroup.items.forEach((item, index) => {
                if(item.key === 'divider') {
                    pr.tl({ content: 'divider' })
                    return
                }

                pr.tl({ content: this.t('reports.body.data.items', item.key) })
                item.values.forEach((val, index) => {
                    const align = dataGroup.columns[index + 1]?.align || 'L'
                    pr.tl({ content: val, align })
                })

                item.additional.forEach(a => {
                    pr.tl({ content: `    ${this.t('reports.body.data.items.additional', a.key)}: ${a.value}`, colSpan: dataGroup.columns.length })
                })
            })

            if(dataGroup.items.length) {
                pr.l('D', '', { delimiter: '-' })
            }

            dataGroup.summary.forEach(summaryItem => {
                pr.l('S', this.t('reports.body.data.summary', summaryItem.key), summaryItem.value)
            })

            if(dataGroup.summary.length) {
                pr.l('D', '', { delimiter: '-' })
            }
        })

        return pr
    }

    getTaxesPrintable(reportData) {
        const pr = new Printable()

        if(!reportData.taxes) return pr

        reportData.taxes.forEach(tax => {
            if(tax.rate === 'total') {
                pr.l('L', i18n.t('reports.body.taxes.sum'), { align: 'C' })
            } else {
                pr.l('L', `${i18n.t('reports.body.taxes.rate')} - ${tax.rate}%`, { align: 'C' })
            }

            pr.l('D', '', { delimiter: '-' })

            pr.l('S', `${i18n.t('reports.body.taxes.total_price_before_tax')}:`, tax.total_price_before_tax)
            pr.l('S', `${i18n.t('reports.body.taxes.total_tax')}:`, tax.total_tax)
            pr.l('S', `${i18n.t('reports.body.taxes.total')}:`, tax.total)
            pr.l('D', '', { delimiter: '-' })
        })

        return pr
    }

    getPaymentsPrintable(reportData) {
        const pr = new Printable()

        if(!reportData.payments) return pr

        const noncashOnly = !!Store.getters.tenant?.venue?.settings?.noncash_only;
        const einvoiceReport = reportData.type === 'einvoice_report';

        // Cash Payments
        const einvoice = EinvoiceService.einvoiceOnly() || einvoiceReport;
        const cashEnabled = !noncashOnly && !einvoice;

        const types = [
            { type: 'banknote', category: 'cash', enabled: cashEnabled },
            { type: 'debt_skipped', category: 'cash', enabled: cashEnabled && locale === 'sr' },
            { type: 'card', category: 'cash', enabled: cashEnabled },
            { type: 'order', category: 'cash', enabled: cashEnabled && locale === 'me' },
            { type: 'other_cash', category: 'cash', enabled: cashEnabled && locale === 'me' },
            { type: 'account', category: 'noncash', enabled: true },
            { type: 'business_card', category: 'noncash', enabled: locale === 'me' },
            { type: 'company', category: 'noncash', enabled: locale === 'me' },
            { type: 'order', category: 'noncash', enabled: locale === 'me' },
            { type: 'factoring', category: 'noncash', enabled: locale === 'me' },
            { type: 'advance', category: 'noncash', enabled: true },
            { type: 'voucher', category: 'noncash', enabled: !einvoice },
            { type: 'check', category: 'noncash', enabled: locale !== 'me' && !einvoice },
            { type: 'mobile_money', category: 'noncash', enabled: locale !== 'me' && !einvoice },
            { type: 'other', category: 'noncash', enabled: locale !== 'me' && !einvoice },
            { type: 'tip', category: 'cash', enabled: cashEnabled },
        ]

        pr.l('L', i18n.t('reports.body.payments.heading'), { align: 'C' })
        pr.l('D', '', { delimiter: '-' })

        types.filter(t => t.enabled).forEach(({ type, category }) => {
            if(reportData.payments[category]) {
                pr.l('S', `${i18n.t(`reports.body.payments.${category}.${type}`)}:`, reportData.payments[category][type] || '0.00')
            }
        })

        pr.l('D', '', { delimiter: '-' })

        return pr
    }

    getJournalPrintable(reportData) {
        const pr = new Printable()

        if(!reportData.journal_items) return pr

        reportData.journal_items.forEach(journalItem => {
            if(journalItem.type === 'cashTransaction') {
                const transaction = new CashRegisterTransaction(journalItem.item)
                pr.l('L', `${i18n.tc('general.time')}: ${this.formatDateTime(journalItem.timestamp)}`)
                if(transaction.getAmount().toUnit() >= 0) {
                    pr.l('L', `${i18n.t('reports.body.journal.deposit')}: ${transaction.getAmount().toFormat(this.priceFormat)}`)
                } else {
                    pr.l('L', `${i18n.t('reports.body.journal.widraw')}: ${transaction.getAmount().multiply(-1).toFormat(this.priceFormat)}`)
                }
                pr.l('D', '', { delimiter: '-' })
            } else if(journalItem.type === 'order') {
                const order = new Order(journalItem.item)

                pr.l('L', `${i18n.tc('general.time')}: ${this.formatDateTime(journalItem.timestamp)}`)
                pr.l('L', `${i18n.tc('general.receipt')}: ${order.number}`)

                // TODO replace below with the printable when implemented
                // const orderPrintableContent = order.getPrintable({ isReceipt: true, isCopy: true }).getPrintableContent()
                // pr.addPrintableContent(orderPrintableContent)

                order.toText({ isReceipt: true, isCopy: true }).split('\n').forEach(line => {
                    pr.l('L', line)
                })

                pr.l('D', '', { delimiter: '-' })
            }
        })

        return pr
    }

    getAdditionalFooterPrintable(reportData) {
        const pr = new Printable()

        if(
            (locale !== 'me' || (!reportData.business_day_number && !reportData.first_business_day)) &&
            !reportData.first_order_number
        ) return pr

        if(locale === 'me') {
            if(reportData.business_day_number) {
                pr.l('S', `${i18n.t('reports.body.footer.business_day_number')}:`, `${reportData.business_day_number}`)
            }

            if(reportData.first_business_day) {
                pr.l('S', `${i18n.t('reports.body.footer.business_day_numbers')}:`, `${reportData.first_business_day} - ${reportData.last_business_day}`)
            }
        }

        if(reportData.first_order_number) {
            pr.l('S', `${i18n.t('reports.body.footer.receipt_numbers')}:`, `${reportData.first_order_number} - ${reportData.last_order_number}`)
        }

        pr.l('D', '', { delimiter: '-' })

        return pr
    }

    getFooterPrintable(reportData) {
        const pr = new Printable()

        pr.l('L', `${i18n.t('reports.body.footer.date')}: ${this.formatDate(reportData.date)}`)
        pr.l('L', `${i18n.t('reports.body.footer.time')}: ${reportData.time}`)

        if(locale === 'me') {
            pr.l('L', `${i18n.t('reports.body.footer.operator_code')}: ${reportData.operator_code}`)
        }

        return pr
    }

    isSalesReport() {
        const salesReportTypes = [
            'total_sales',
            'sales_by_client',
            'sales_by_user',
            'sales_by_product',
            'sales_by_product_category',
            'sales_by_product_type',
            'sales_by_product_type_sent_to_room',
            'sales_by_product_type_and_taxes',
            'sales_by_sector',
            'sales_by_ingredient',
            'sales_by_user_detailed',
        ]

        return salesReportTypes.includes(this.type)
    }

    getTimezone() {
        const timezone = Store.getters?.tenant?.venue?.timezone
        if(timezone) {
            return timezone
        }

        switch (locale) {
            case 'me':
                return 'Europe/Podgorica';
            case 'sr':
                return 'Europe/Belgrade';
            case 'ba':
                return 'Europe/Sarajevo';
            default:
                return 'UTC';
        }
    }

    formatDateTime(date) {
        if(!(date instanceof moment)) {
            date = moment(date)
        }

        return ['me', 'sr', 'ba'].includes(locale)
            ? date.format('DD.MM.YYYY. HH:mm:ss')
            : date.format('YYYY/MM/DD HH:mm:ss')
    }

    formatDate(date) {
        if(!(date instanceof moment)) {
            date = moment(date)
        }

        return ['me', 'sr', 'ba'].includes(locale)
            ? date.format('DD.MM.YYYY.')
            : date.format('YYYY/MM/DD')
    }

    formatDateTimeHuman(date) {
        if(!(date instanceof moment)) {
            date = moment(date)
        }

        return ['me', 'sr', 'ba'].includes(locale)
            ? date.format('D MMM HH:mm')
            : date.format('D MMM HH:mm')
    }

    t(prefix, key) {
        return i18n.t(`${prefix}.${key}`) !== `${prefix}.${key}`
            ? i18n.t(`${prefix}.${key}`)
            : key
    }
}

export default Report
